import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api.js";
import { Skeleton } from "@bongeunsa/ui";
import { Link } from "react-router-dom";
import type { FunnelStats } from "@bongeunsa/db";
import { QRDropdown } from "@/components/QRDropdown.js";

type DashboardData = {
  active_page: { id: string; name: string; block_count: number } | null;
  active_test: { id: string; name: string; slug: string } | null;
  active_survey: { id: string; name: string; slug: string } | null;
  funnel: FunnelStats | null;
};

type Demographics = {
  language: Record<string, number>;
  resultTypes: Array<{ slug: string; count: number; i18n: string }>;
};

const LANG_FLAGS: Record<string, string> = { ko: "🇰🇷", en: "🇺🇸", zh: "🇨🇳", ja: "🇯🇵" };
const PUBLIC_BASE = import.meta.env.DEV ? "http://localhost:5173" : "https://bongeunsa.app";

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: () => api.get<DashboardData>("/api/admin/dashboard"),
    refetchInterval: 30_000,
  });

  const activeTestId = data?.active_test?.id;
  const { data: demographics } = useQuery({
    queryKey: ["dashboard-demographics", activeTestId],
    queryFn: () =>
      api.get<Demographics>(`/api/stats/demographics?survey_id=${activeTestId}&type=test`),
    enabled: !!activeTestId,
    staleTime: 30_000,
  });

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-8">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">대시보드</h1>

        {/* ── 펀넬 통계 ──────────────────────────────────── */}
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
            : [
                { label: "메인 진입", value: data?.funnel?.main_views ?? 0 },
                { label: "검사 시작", value: data?.funnel?.survey_starts ?? 0 },
                { label: "동의 완료", value: data?.funnel?.consents ?? 0 },
                { label: "검사 완주", value: data?.funnel?.completions ?? 0 },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-black/5">
                  <p className="text-xs font-medium text-gray-500">{label}</p>
                  <p className="mt-1 text-3xl font-bold text-gray-900">{value.toLocaleString()}</p>
                  <p className="mt-1 text-xs text-gray-400">누적</p>
                </div>
              ))}
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">

          {/* ── 메인페이지 섹션 ────────────────────────── */}
          <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">액티브 메인페이지</p>
                {isLoading ? (
                  <Skeleton className="mt-1 h-5 w-32" />
                ) : data?.active_page ? (
                  <p className="mt-0.5 font-semibold text-gray-900">{data.active_page.name}</p>
                ) : (
                  <p className="mt-0.5 text-sm text-gray-400">없음</p>
                )}
              </div>
              {data?.active_page && (
                <div className="flex gap-2">
                  <Link
                    to={`/main-pages/${data.active_page.id}/edit`}
                    className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
                  >
                    편집
                  </Link>
                  <a
                    href={`${PUBLIC_BASE}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    사이트 ↗
                  </a>
                  <QRDropdown url={`${PUBLIC_BASE}/`} />
                </div>
              )}
            </div>

            {data?.active_page ? (
              <div className="flex gap-4 p-4">
                <div className="shrink-0">
                  <p className="mb-1.5 text-xs text-gray-400">모바일 미리보기</p>
                  <div
                    className="overflow-hidden rounded-2xl shadow-md ring-1 ring-black/10"
                    style={{ width: 160, height: 284 }}
                  >
                    <iframe
                      src={`${PUBLIC_BASE}/`}
                      width={375}
                      height={667}
                      className="block origin-top-left"
                      style={{ transform: "scale(0.427)", width: 375, height: 667 }}
                      title="메인 미리보기"
                    />
                  </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <div>
                    <p className="mb-1.5 text-xs text-gray-400">OG 이미지</p>
                    <div className="overflow-hidden rounded-xl ring-1 ring-black/10">
                      <img
                        src="/api/og/main?lang=ko"
                        alt="OG 이미지"
                        className="w-full object-cover"
                        style={{ aspectRatio: "1200/630" }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-gray-500">
                    <p>블록: <span className="font-semibold text-gray-700">{data.active_page.block_count}개</span></p>
                    {data.active_test && (
                      <p className="rounded-lg bg-brand-50 px-2 py-1 text-brand-700">
                        ↳ 검사 연결됨: {data.active_test.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <p className="text-sm text-gray-400">액티브 메인페이지가 없습니다.</p>
                <Link to="/main-pages" className="mt-2 inline-block text-xs text-brand-500 hover:underline">
                  생성하기 →
                </Link>
              </div>
            )}
          </div>

          {/* ── 검사 섹션 ─────────────────────────────── */}
          <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">액티브 검사</p>
                {isLoading ? (
                  <Skeleton className="mt-1 h-5 w-32" />
                ) : data?.active_test ? (
                  <p className="mt-0.5 font-semibold text-gray-900">{data.active_test.name}</p>
                ) : (
                  <p className="mt-0.5 text-sm text-gray-400">없음</p>
                )}
              </div>
              {data?.active_test && (
                <div className="flex gap-2">
                  <Link
                    to={`/tests/${data.active_test.id}/edit`}
                    className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
                  >
                    편집
                  </Link>
                  <a
                    href={`${PUBLIC_BASE}/t/${data.active_test.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    사이트 ↗
                  </a>
                  <QRDropdown url={`${PUBLIC_BASE}/t/${data.active_test.slug}`} />
                </div>
              )}
            </div>

            {data?.active_test ? (
              <div className="flex gap-4 p-4">
                <div className="shrink-0">
                  <p className="mb-1.5 text-xs text-gray-400">검사 미리보기</p>
                  <div
                    className="overflow-hidden rounded-2xl shadow-md ring-1 ring-black/10"
                    style={{ width: 160, height: 284 }}
                  >
                    <iframe
                      src={`${PUBLIC_BASE}/t/${data.active_test.slug}`}
                      width={375}
                      height={667}
                      className="block origin-top-left"
                      style={{ transform: "scale(0.427)", width: 375, height: 667 }}
                      title="검사 미리보기"
                    />
                  </div>
                </div>

                <div className="min-w-0 flex-1 space-y-4">
                  {demographics?.language && Object.keys(demographics.language).length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-semibold text-gray-500">언어별 완주</p>
                      <div className="space-y-1.5">
                        {Object.entries(demographics.language)
                          .sort(([, a], [, b]) => b - a)
                          .map(([lang, count]) => {
                            const total = Object.values(demographics.language).reduce((a, b) => a + b, 0);
                            const pct = Math.round((count / (total || 1)) * 100);
                            return (
                              <div key={lang} className="flex items-center gap-2">
                                <span className="w-5 text-sm">{LANG_FLAGS[lang] ?? "🌐"}</span>
                                <div className="flex-1 overflow-hidden rounded-full bg-gray-100">
                                  <div className="h-1.5 rounded-full bg-brand-400" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="w-6 text-right text-xs text-gray-500">{count}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  {demographics?.resultTypes && demographics.resultTypes.filter((r) => r.count > 0).length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-semibold text-gray-500">결과 유형 분포</p>
                      <div className="space-y-1.5">
                        {demographics.resultTypes
                          .filter((rt) => rt.count > 0)
                          .sort((a, b) => b.count - a.count)
                          .slice(0, 5)
                          .map((rt) => {
                            const total = demographics.resultTypes.reduce((a, b) => a + b.count, 0);
                            const pct = Math.round((rt.count / (total || 1)) * 100);
                            let label = rt.slug;
                            try { label = (JSON.parse(rt.i18n) as Record<string, Record<string, string>>).ko?.title ?? rt.slug; } catch {}
                            return (
                              <div key={rt.slug} className="flex items-center gap-2">
                                <span className="w-12 truncate text-xs text-gray-600">{label}</span>
                                <div className="flex-1 overflow-hidden rounded-full bg-gray-100">
                                  <div className="h-1.5 rounded-full bg-brand-300" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="w-5 text-right text-xs text-gray-500">{rt.count}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  {!demographics && (
                    <p className="text-xs text-gray-300">응답 데이터 없음</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <p className="text-sm text-gray-400">액티브 검사가 없습니다.</p>
                <Link to="/tests" className="mt-2 inline-block text-xs text-brand-500 hover:underline">
                  생성하기 →
                </Link>
              </div>
            )}
          </div>

          {/* ── 액티브 설문 (있을 경우) ────────────────── */}
          {data?.active_survey && (
            <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">액티브 설문</p>
                  <p className="mt-0.5 font-semibold text-gray-900">{data.active_survey.name}</p>
                </div>
                <div className="flex gap-2">
                  <Link
                    to={`/surveys/${data.active_survey.id}/edit`}
                    className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
                  >
                    편집
                  </Link>
                  <a
                    href={`${PUBLIC_BASE}/s/${data.active_survey.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    사이트 ↗
                  </a>
                </div>
              </div>
              <div className="p-4">
                <p className="text-xs text-gray-500">
                  URL: <span className="font-mono text-gray-700">{PUBLIC_BASE}/s/{data.active_survey.slug}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
